import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Target, HeartHandshake, GraduationCap, Users, FlaskConical } from "lucide-react";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { PageHero, SectionHeading, FinalCTA } from "@/components/sections";
import { Reveal, stagger, item } from "@/components/motion";
import { SITE } from "@/lib/site";

const CHAPTERS = [
  { n: "01", icon: Compass, title: "Our Mission", text: "To make evidence-based mental healthcare feel human — giving every person in Connecticut practical, proven tools to change unhelpful patterns and build lasting wellbeing." },
  { n: "02", icon: Target, title: "Our Vision", text: "To become Connecticut's most trusted CBT practice: the place people turn to when they're ready not just to talk, but to genuinely change." },
  { n: "03", icon: HeartHandshake, title: "Collaborative Care", text: "You are the expert on your life; we're the experts on change. Great therapy happens when those two meet as equals, working toward goals you define." },
  { n: "04", icon: GraduationCap, title: "A Teaching Philosophy", text: "We believe the goal of therapy is to make ourselves unnecessary. We teach skills you keep for life — so you leave more capable, confident, and self-reliant." },
  { n: "05", icon: FlaskConical, title: "Evidence, Always", text: "Every method we use is grounded in research and tracked with measurement-based care. If it isn't proven and isn't working, we adjust." },
  { n: "06", icon: Users, title: "Community", text: "Mental health is a community effort. We're proud to serve Southington and all of Connecticut with accessible, compassionate, results-focused care." },
];

export default function About() {
  const crumbs = [{ name: "Home", path: "/" }, { name: "About", path: "/about" }];
  return (
    <>
      <SEO title="About Us" path="/about"
        description="Beacon Counseling is Connecticut's evidence-based CBT practice. Learn about our mission, values, collaborative approach, and teaching philosophy."
        jsonLd={[orgSchema, breadcrumbSchema(crumbs)]} />
      <PageHero eyebrow="Our Story" breadcrumb={crumbs}
        title="Turning obstacles into opportunities — with proof, not just talk."
        intro="Beacon Counseling was founded on a simple belief: therapy should give you tools, direction, and measurable change — not just a place to vent."
        image="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />

      {/* Intro statement */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-12">
          <Reveal>
            <p className="font-display text-3xl leading-snug tracking-tight text-navy sm:text-4xl lg:text-[2.75rem] text-balance">
              We started Beacon because too many people spend years in therapy without ever being handed
              the <span className="text-teal italic">practical skills</span> to actually feel better. We built a different kind of practice — one grounded in evidence, focused on outcomes, and full of genuine warmth.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Numbered manifesto */}
      <section className="bg-secondary py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading eyebrow="What We Stand For" title="Six principles that shape every session." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-16 grid gap-x-12 gap-y-14 md:grid-cols-2">
            {CHAPTERS.map((c) => (
              <motion.div key={c.n} variants={item} className="group flex gap-6">
                <div className="shrink-0">
                  <div className="font-display text-5xl text-teal/25 transition-colors group-hover:text-teal/50">{c.n}</div>
                  <span className="mt-3 grid h-12 w-12 place-items-center rounded-2xl bg-white text-teal shadow-soft">
                    <c.icon className="h-6 w-6" />
                  </span>
                </div>
                <div className="border-b border-navy-100 pb-8">
                  <h3 className="font-display text-2xl text-navy">{c.title}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{c.text}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Our Space" title="A calm, modern place to do real work." />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
              "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
              "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
            ].map((src, i) => (
              <Reveal key={src} delay={i * 0.08}>
                <img src={src} alt="Beacon Counseling environment"
                  className={`w-full rounded-3xl object-cover shadow-soft ${i === 1 ? "aspect-[3/4] sm:col-span-1 lg:row-span-1" : "aspect-[4/3]"}`} />
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 text-center text-muted-foreground">
              Prefer to meet from home? We also offer secure <Link to="/telehealth" className="font-medium text-teal hover:underline">Telehealth</Link> across Connecticut.
            </p>
          </Reveal>
        </div>
      </section>

      <FinalCTA title="Come as you are." text="Wherever you're starting from, there's a path forward — and we'd be honoured to walk it with you." />
    </>
  );
}
